import { useInfiniteQuery } from "@tanstack/react-query";
import { debounce, sortBy, uniqBy } from "lodash-es";
import { type CSSProperties, type UIEventHandler, useCallback, useState } from "react";
import { type PermissionNames, PermissionOperations } from "~enums/permissions";
import { useApi } from "~hooks/apiHooks/useApi";
import { useUserPermissions } from "~context/UserPermissionsContext";
import type { Response } from "~types/response";
import classNames from "~utils/classNames";
import CustomSelect from "../CustomSelect/CustomSelect";
import styles from "./InfinitySelect.module.css";

type KeyValuePair = { [key: string]: string | undefined | null };
type InfinitySelectOptionValue = string;

export type InfinitySelectOption = {
  label: string;
  value: InfinitySelectOptionValue;
  disabled?: boolean;
};

type Props<TResponse> = {
  url: string;
  includes?: string[];
  filterParameters?: KeyValuePair;
  sortByFieldName?: string;
  limit?: number;
  searchParameter?: string;
  placeholder?: string;
  values?: InfinitySelectOption[];
  allowClear?: boolean;
  disabled?: boolean;
  maxCount?: number;
  queryClientPrefixKey?: string[];
  onChange?: (value: InfinitySelectOption | null) => void;
  onChangeMultiple?: (values: InfinitySelectOption[]) => void;
  onConvertResponseIntoOptions: (response: TResponse) => InfinitySelectOption[];
  /** Adds options to the options from server. If added, filtering (to avoid duplicates) and client sorting (may not match BE sorting) will be performed. */
  additionalOptions?: InfinitySelectOption[];
  permissionName: PermissionNames;
  permissionOperation?: PermissionOperations;
  rightAligned?: boolean;
  resetOptionsDependencies?: Array<unknown>;
  disabledOptionValues?: InfinitySelectOptionValue[];
  style?: CSSProperties;
  className?: string;
};

export const InfinitySelect = <TResponseData extends Response<unknown>>({
  allowClear = true,
  limit = 50,
  maxCount = 1,
  ...props
}: Props<TResponseData>) => {
  const request = useApi();

  const [isDropDownOpen, setIsDropDownOpen] = useState(false);

  const [searchValue, setSearchValue] = useState("");
  const setSearchValueDebounced = useCallback(debounce(setSearchValue, 500), []);

  const { hasPermission } = useUserPermissions();
  const hasAccess = hasPermission(props.permissionName, props.permissionOperation ?? PermissionOperations.READ);
  const isDisabled = props.disabled || !hasAccess;

  const getUrl = (offset: number) => {
    const params = new URLSearchParams();
    params.set("limit", limit.toString());
    params.set("offset", offset.toString());

    if (props.filterParameters) {
      Object.entries(props.filterParameters)
        .filter(([key, val]) => key && val)
        .forEach(([key, val]) => {
          params.set(key, val as string);
        });
    }

    if (props.includes && props.includes.length > 0) {
      params.set("include", props.includes.join(","));
    }

    if (props.sortByFieldName) {
      params.set("sortBy", props.sortByFieldName);
    }

    if (props.searchParameter && searchValue.length >= 2) {
      params.set(props.searchParameter, searchValue);
    }

    return `${props.url}?${params.toString()}`;
  };

  const { data, fetchNextPage, hasNextPage, isFetching, isError } = useInfiniteQuery({
    enabled: isDropDownOpen,
    queryKey: [
      ...(props.queryClientPrefixKey ?? []),
      "infinity-select",
      getUrl(0),
      ...(props.resetOptionsDependencies ? [props.resetOptionsDependencies.join()] : []),
    ],
    queryFn: ({ pageParam }) =>
      request<TResponseData>(getUrl(pageParam)).then((response) => ({
        options: props.onConvertResponseIntoOptions(response),
        currentPage: pageParam,
        nextPage: response.links.next ? pageParam + limit : null,
      })),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextPage,
    staleTime: () => 5 * 60 * 1000,
  });

  const rawOptions = data?.pages.flatMap((page) => page.options) ?? [];
  const withAdditionalOptions = uniqBy(
    props.additionalOptions ? [...rawOptions, ...props.additionalOptions] : rawOptions,
    (x) => x.value,
  );
  const sortedOptionsIfNecessary =
    !props.sortByFieldName || (props.additionalOptions || []).length > 0
      ? sortBy(withAdditionalOptions, (x) => x.label?.toLowerCase())
      : withAdditionalOptions;
  const options = props.disabledOptionValues
    ? sortedOptionsIfNecessary.map((option) => ({
        ...option,
        disabled: (props.disabledOptionValues as string[]).some((el) => el === option.value),
      }))
    : sortedOptionsIfNecessary;

  const handlePopupScroll: UIEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLElement;
    if (target.scrollTop + target.offsetHeight >= target.scrollHeight * 0.9 && hasNextPage && !isFetching) {
      fetchNextPage();
    }
  };

  const onChangeHandler = (value: unknown | unknown[]) => {
    setSearchValue("");
    if (maxCount > 1) {
      props.onChangeMultiple?.(Array.isArray(value) ? value : value ? [value] : []);
    } else {
      const val = Array.isArray(value) ? value[0] : value;
      props.onChange?.(val ?? null);
    }
  };

  return (
    <CustomSelect
      className={classNames(styles.infinitySelect, props.rightAligned && styles.rightAligned, props.className)}
      style={props.style}
      allowClear={maxCount > 1 || allowClear}
      maxCount={maxCount !== 1 ? maxCount : undefined}
      mode={maxCount > 1 ? "multiple" : undefined}
      labelInValue
      filterOption={false}
      showSearch={!!props.searchParameter}
      options={options}
      placeholder={props.placeholder}
      loading={isFetching}
      value={props.values ?? []}
      disabled={isDisabled}
      onSearch={props.searchParameter ? setSearchValueDebounced : undefined}
      onPopupScroll={handlePopupScroll}
      onChange={onChangeHandler}
      onDropdownVisibleChange={setIsDropDownOpen}
      popupRender={(menu) => (props.rightAligned ? <div className={styles.stickRight}>{menu}</div> : menu)}
      {...(isError && { status: "error" })}
    />
  );
};
